import React, { useState, useEffect } from 'react';
import {
  User as UserIcon,
  ChevronDown,
  ChevronLeft,
  Crown,
  LogOut,
  Briefcase,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { closeSidebar } from '../../store/uiSlice';
import { logoutUser } from '../../store/userSlice';
import { getManagerOrganizations } from '../../api/organizationApi';
import { selectBetaFeatureEnabled } from '../../store/featureSlice';
import './SideBar.css';
import { capitalizeText } from '../../utils/capitalizeText';
import { formatPlanLabel, isPaidPlanCode, isStarterPlanCode } from '../../utils/billingPlans';

export default function Sidebar({ navLinks = [] }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isOpen = useSelector((s) => s.ui.sidebarOpen);
  const user = useSelector((s) => s.user?.data);
  const betaFeatureEnabled = useSelector(selectBetaFeatureEnabled);
  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString()
    : '';
  const rawPlanLabel = (user?.planName || user?.billing?.plan?.name || user?.billing?.plan?.code || 'Starter').toString();
  const planCode = user?.billing?.plan?.code || '';
  const isPaidPlan = isPaidPlanCode(planCode, rawPlanLabel);
  const isStarterPlan = isStarterPlanCode(planCode, rawPlanLabel);
  const planLabel = formatPlanLabel({ rawLabel: rawPlanLabel, planCode, betaFeatureEnabled });

  const [mgrOrgId, setMgrOrgId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "home";
  // ✅ Fetch orgs if user is manager
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await getManagerOrganizations();
        if (alive && Array.isArray(data) && data.length) {
          setMgrOrgId(data[0].id);
        }
      } catch {
        if (alive) setMgrOrgId(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ Remember collapse state
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved != null) setIsCollapsed(saved === '1');
  }, []);
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed ? '1' : '0');
  }, [isCollapsed]);

  // ✅ Disable body scroll on mobile when open
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [isOpen]);

  // ✅ Logout
  const onLogout = async () => {
    await dispatch(logoutUser());
    window.location.replace('/auth');
  };

  const handleNavigate = (to) => {
    navigate(to.startsWith('/') ? to : `/${to}`);
    dispatch(closeSidebar());
  };

  if (!user) return null;
  return (
    <>
      {isOpen && (
        <div className="sidebar-backdrop" onClick={() => dispatch(closeSidebar())} />
      )}

      <aside
        className={`dashboard-sidebar ${isOpen ? 'open' : ''} ${
          isCollapsed ? 'collapsed' : ''
        }`}
      >
        {/* Collapse toggle (desktop only) */}
        <button
          className="collapse-toggle"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setIsCollapsed((v) => !v)}
        >
          <ChevronLeft size={24} />
        </button>

        {/* Profile Section */}
        <div className="sidebar-header profile-sidebar-header">
          <div className={`account-accordion ${isAccountOpen ? 'open' : ''}`}>
            <div className="account-head">
              <button
                type="button"
                className="account-profile-link"
                onClick={() => handleNavigate('/user-profile')}
                title={isCollapsed ? `${fullName || 'User profile'}` : 'Open user profile'}
              >
                <div className="account-head-left">
                  <span className="profile-avatar">
                    {fullName?.trim()
                      ? fullName
                          .trim()
                          .split(/\s+/)
                          .map((n) => n[0].toUpperCase())
                          .join('')
                      : <UserIcon size={18} />}
                  </span>
                  <div className="profile-ident">
                    <div className="profile-name">
                      {capitalizeText(fullName) || '—'}
                    </div>
                    <div className="profile-sub">Member since {memberSince}</div>
                  </div>
                </div>
              </button>
              {!isCollapsed && (
                <button
                  type="button"
                  className="account-toggle"
                  onClick={() => setIsAccountOpen((v) => !v)}
                  aria-expanded={isAccountOpen}
                  aria-controls="account-panel"
                  aria-label={isAccountOpen ? 'Close account menu' : 'Open account menu'}
                >
                  <ChevronDown
                    size={18}
                    className={`account-chevron ${
                      isAccountOpen ? 'rotated' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>

            {!isCollapsed && isAccountOpen && (
              <div id="account-panel" className="account-panel animate-slide-down">
                <div className="account-row">
                  <UserIcon className="dropdown-icon" />
                  <span className="account-text truncate">{user?.email || ''}</span>
                </div>
                <div className="account-sep" />

                <div className="account-row">
                  <Crown className="dropdown-icon" />
                  <span className="account-text">Current Plan</span>
                  <span
                    className={`plan-badge ${
                      isPaidPlan
                        ? 'badge-pro'
                        : isStarterPlan
                        ? 'badge-secondary'
                        : 'badge-enterprise'
                    }`}
                  >
                    {planLabel}
                  </span>
                </div>

                {mgrOrgId && (
                  <>
                    <div className="account-sep" />
                    <button
                      className="account-row account-link"
                      onClick={() => handleNavigate(`/org/${mgrOrgId}/management?tab=organization`)}
                    >
                      <Briefcase className="dropdown-icon" />
                      <span>Management Dashboard</span>
                    </button>
                  </>
                )}

                <div className="account-sep" />
                <button
                  className="account-row account-link danger"
                  onClick={onLogout}
                >
                  <LogOut className="dropdown-icon" />
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="sidebar-nav">
  {navLinks.map((link) => {
    const url = new URL(link.to, window.location.origin);
    const tabValue = new URLSearchParams(url.search).get("tab");
    const isActive = tabValue
      ? tabValue === currentTab
      : location.pathname === url.pathname;

    return (
      <button
        key={link.label}
        className={`sidebar-link${isActive ? " active" : ""}`}
        onClick={() => {
          navigate(link.to);
          dispatch(closeSidebar());
        }}
        data-tooltip={isCollapsed ? link.label : undefined}
      >
        <span className="sidebar-link-icon">{link.icon}</span>
        <span className="sidebar-link-label">{link.label}</span>
      </button>
    );
  })}
</nav>
      </aside>
    </>
  );
}
